import { useCloudSourceInfo } from '@stores/cloudSourcesInfo/cloudSourcesInfo';
import React, { useState } from 'react';
import moment from 'moment';
import { IDestination } from '@stores/destination';
import { Skeleton } from 'antd';
import { Flex } from '@ui-library/misc';
import Loading from '@ui-library/Loading';
import NoDestinationTabView from '@components/sources/source-view/source-destinations/noDestinationTabView';
import { SYNC_BEHAVIOUR_MODES } from '@components/sources/visualizer/mappingScreens/visualMapping';
import ReportErrorsModalView, { UnsuccessfulEventErrorProps } from './errors';
import { IReport } from './Report';
import Syncs from './Syncs';
import DestinationSelect from './DestinationSelect';
import MetricModal, { MetricProps } from './metricModal';

const CloudSourceReports = (isAdmin: boolean) => {
  const { runHistory, source, nextSyncAt, initializeUpAndDownDirectionRunHistory } =
    useCloudSourceInfo();
  const destinations = source?.destinations || [];

  let defaultDestination = destinations.find((d) => d.enabled);
  if (!defaultDestination) {
    [defaultDestination] = destinations;
  }
  const defaultDate = {
    from: moment().add(-1, 'd').format('YYYY-MM-DD 00:00:00'),
    to: moment().format('YYYY-MM-DD 23:59:59'),
  };
  const [date, setDate] = React.useState(defaultDate);
  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [destination, setDestination] = React.useState<IDestination>(defaultDestination);
  const onChangeDestination = React.useCallback(
    (destinationId: string) => {
      const selectedDestination = destinations.find((d) => d.id === destinationId);
      setDate(defaultDate);
      setDestination(selectedDestination!);
    },
    [destinations],
  );
  React.useEffect(() => {
    if (!runHistory) {
      setLoading(true);
    } else {
      setUpdating(true);
    }
    if (defaultDestination) {
      initializeUpAndDownDirectionRunHistory(destination.id, date.from, date.to).finally(() => {
        setLoading(false);
        setUpdating(false);
      });
    }
  }, [date, destination]);
  const [rejectedModalData, setRejectedModalData] = useState<
    UnsuccessfulEventErrorProps | undefined
  >();
  const [operationMetrices, setOperationMetrices] = useState<MetricProps>({});
  const handleCloseModal = () => {
    setRejectedModalData(undefined);
  };

  const enableOnClickRecords = source.config?.syncBehaviour?.mode === SYNC_BEHAVIOUR_MODES.MIRROR;

  const hideMetricModal = () => {
    setOperationMetrices({});
  };
  let metricModal = null;
  if (operationMetrices.type) {
    metricModal = <MetricModal data={operationMetrices} onClose={hideMetricModal} />;
  }
  let rejectedModal = null;
  if (rejectedModalData) {
    rejectedModal = (
      <ReportErrorsModalView
        sourceId={source.id || ''}
        jobRunId={rejectedModalData.jobRunId}
        taskRunId={rejectedModalData.taskRunId}
        type={rejectedModalData.type}
        onClose={handleCloseModal}
        startedAt={rejectedModalData.startedAt}
      />
    );
  }
  if (destinations.length === 0) {
    return <NoDestinationTabView tabName="SYNCS" sourceID={source?.id} />;
  }
  const sourceName = source!.name!;
  const destinationName = destination?.name;

  const loadingData = loading || !source || !runHistory;
  const reports = runHistory as IReport[];
  return (
    <div>
      {rejectedModal}
      {metricModal}
      {loadingData ? (
        <Flex style={{ width: '100%' }}>
          <Skeleton.Input style={{ width: 200 }} active />
          <div style={{ marginLeft: 'auto' }}>
            <Skeleton.Input style={{ width: 100 }} active />
          </div>
        </Flex>
      ) : (
        <DestinationSelect
          sourceRole={source!.sourceDef!.name!}
          sourceName={sourceName}
          defaultDestinationId={destination.id}
          destinations={source.destinations || []}
          onChange={onChangeDestination}
          nextSyncAt={nextSyncAt}
          isAdmin={isAdmin}
        />
      )}

      <div style={{ marginTop: '3rem' }}>
        {loadingData ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Loading spinning={updating}>
            <Syncs
              sourceCreatedAt={source.createdAt!}
              reports={reports}
              onChangeDate={setDate}
              sourceName={sourceName}
              destinationName={destinationName}
              onClickRejected={setRejectedModalData}
              onClickRecords={enableOnClickRecords ? setOperationMetrices : undefined}
              operationMetrices={operationMetrices}
            />
          </Loading>
        )}
      </div>
    </div>
  );
};

export default CloudSourceReports;
